/**
 * Business profiles: the letterhead data (logo, address, contact) printed on a workspace's
 * generated documents. Owner and admin seats only; the callables refuse everyone else.
 */

import { useState } from "react";
import { Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Plus } from "lucide-react";
import { PageHeader } from "../components/layout/PageHeader";
import { BusinessProfileForm } from "../components/business/BusinessProfileForm";
import { KiiBadge, KiiConfirmDialog, KiiDialogLayout } from "../components/kii";
import { useWorkspace } from "../contexts/WorkspaceContext";
import { useWorkspaceUrl } from "../lib/routes";
import { contactCandidates, logProfileError, profileErrorKey } from "../lib/businessProfiles";
import { useIsWorkspaceAdmin, useWorkspaceCollaborators } from "../hooks/useWorkspaceMembers";
import { useBusinessProfiles, useDeleteBusinessProfile, useUpdateBusinessProfile } from "../hooks/useBusinessProfiles";
import type { BusinessProfile } from "../services/naraDoor";

const K = "businessProfiles";
const buttonClass = "inline-flex items-center gap-2 px-3 py-2 text-foreground border border-border rounded-lg hover:bg-accent transition-colors disabled:opacity-50";

type Notice = { kind: "ok" | "error"; text: string };

export function BusinessProfiles() {
  const { t } = useTranslation();
  const { activeWorkspace } = useWorkspace();
  const urls = useWorkspaceUrl();
  const { data: collaboratorsData, isLoading: seatsLoading } = useWorkspaceCollaborators();
  const isAdmin = useIsWorkspaceAdmin();
  const { data: profiles = [], isLoading, error: loadError } = useBusinessProfiles(isAdmin);
  const updateProfile = useUpdateBusinessProfile();
  const deleteProfile = useDeleteBusinessProfile();
  // "new", a profile id, or null (closed). Only which row is open: the profile itself is read from the query.
  const [editing, setEditing] = useState<string | null>(null);
  const [toDelete, setToDelete] = useState<BusinessProfile | null>(null);
  const [notice, setNotice] = useState<Notice | null>(null);

  if (seatsLoading) return <div className="h-64 bg-muted rounded-lg animate-pulse" />;
  // A seat that is not owner/admin gets no data: the page never asks the server for it.
  if (!isAdmin) return <Navigate to={urls?.dashboard ?? "/"} replace />;

  const ids = { workspaceId: activeWorkspace?.id };
  const editedProfile = profiles.find((p) => p.id === editing);
  const hasNoDefault = profiles.length > 0 && !profiles.some((p) => p.is_default);

  const makeDefault = async (profile: BusinessProfile) => {
    setNotice(null);
    try {
      await updateProfile.mutateAsync({ profileId: profile.id, changes: { is_default: true } });
      setNotice({ kind: "ok", text: t(`${K}.toast.saved`) });
    } catch (err) {
      logProfileError("updateBusinessProfile", { ...ids, profileId: profile.id }, err);
      setNotice({ kind: "error", text: t(profileErrorKey(err, "save")) });
    }
  };

  const confirmDelete = async () => {
    const profile = toDelete;
    if (!profile) return;
    setToDelete(null);
    setNotice(null);
    try {
      await deleteProfile.mutateAsync(profile.id);
      setNotice({ kind: "ok", text: t(`${K}.toast.deleted`) });
    } catch (err) {
      logProfileError("deleteBusinessProfile", { ...ids, profileId: profile.id }, err);
      setNotice({ kind: "error", text: t(profileErrorKey(err, "delete")) });
    }
  };

  return (
    <div>
      <PageHeader
        title={t(`${K}.title`)}
        description={t(`${K}.intro`)}
        actions={
          <button
            type="button"
            onClick={() => { setNotice(null); setEditing("new"); }}
            className="inline-flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="h-4 w-4" />
            {t(`${K}.add`)}
          </button>
        }
      />

      {notice && (
        <p role={notice.kind === "error" ? "alert" : "status"} className={`mb-4 text-sm ${notice.kind === "error" ? "text-red-600" : "text-green-600"}`}>
          {notice.text}
        </p>
      )}
      {loadError && <p role="alert" className="mb-4 text-sm text-red-600">{t(profileErrorKey(loadError, "load"))}</p>}
      {hasNoDefault && (
        <p className="mb-4 p-3 text-sm bg-amber-50 border border-amber-200 text-amber-700 rounded-lg">{t(`${K}.noDefault`)}</p>
      )}

      {isLoading ? (
        <div className="h-24 bg-muted rounded-lg animate-pulse" />
      ) : profiles.length === 0 && !loadError ? (
        <p className="text-sm text-muted-foreground">{t(`${K}.empty`)}</p>
      ) : (
        <ul className="space-y-3">
          {profiles.map((p) => (
            <li key={p.id} className="flex items-center gap-4 bg-card rounded-lg border border-border p-4">
              <div className="h-12 w-12 shrink-0 flex items-center justify-center rounded border border-border bg-white overflow-hidden">
                {p.logo_url && <img src={p.logo_url} alt="" className="h-full w-full object-contain" />}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-foreground truncate">{p.name}</span>
                  {p.is_default && <KiiBadge variant="success">{t(`${K}.defaultBadge`)}</KiiBadge>}
                </div>
              </div>
              {hasNoDefault && (
                <button type="button" className={buttonClass} disabled={updateProfile.isPending} onClick={() => makeDefault(p)}>
                  {t(`${K}.setDefault`)}
                </button>
              )}
              <button type="button" className={buttonClass} onClick={() => { setNotice(null); setEditing(p.id); }} aria-label={p.name}>
                {t("common.edit")}
              </button>
            </li>
          ))}
        </ul>
      )}

      <KiiDialogLayout
        open={editing !== null && (editing === "new" || !!editedProfile)}
        onOpenChange={(open) => !open && setEditing(null)}
        title={editedProfile?.name ?? t(`${K}.add`)}
        size="2xl"
      >
        {editing !== null && (
          <>
            <BusinessProfileForm
              key={editing}
              profile={editedProfile}
              isFirst={profiles.length === 0}
              candidates={contactCandidates(collaboratorsData?.collaborators ?? [])}
              onCancel={() => setEditing(null)}
              onSaved={(saved, created) => {
                setNotice({ kind: "ok", text: t(`${K}.toast.saved`) });
                // A new profile stays open as an edit: its logo needs the saved profile id.
                setEditing(created ? saved.id : null);
              }}
            />
            {editedProfile && (
              <button type="button" className={`${buttonClass} mt-4 text-red-600`} onClick={() => setToDelete(editedProfile)}>
                {t(`${K}.delete`)}
              </button>
            )}
          </>
        )}
      </KiiDialogLayout>

      <KiiConfirmDialog
        open={toDelete !== null}
        onOpenChange={(open) => !open && setToDelete(null)}
        onConfirm={confirmDelete}
        title={t(`${K}.deleteConfirm.title`)}
        description={t(toDelete?.is_default ? `${K}.deleteConfirm.bodyDefault` : `${K}.deleteConfirm.body`)}
        confirmText={t(`${K}.delete`)}
        cancelText={t(`${K}.cancel`)}
        variant="destructive"
      />
    </div>
  );
}
